// ═══════════════════════════════════════════════════════════════
// LmsPanel — point d'entrée du module Formations (onglet sidebar 'lms')
// Onglets : Catalogue · Mon apprentissage · Espace formateur (si capability)
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, GraduationCap, PenTool, Plus, ShoppingCart, ArrowRight } from 'lucide-react';
import { lmsApi } from '../../services/lmsApi';
import LmsCoursePlayer from './LmsCoursePlayer';
import LmsCourseEditor from './LmsCourseEditor';
import LmsCourseFormModal from './LmsCourseFormModal';

const ACCENT = '#8b5cf6';

export default function LmsPanel({ user }) {
    const isInstructor = user?.role === 'admin_general' || user?.permissions?.lms_instructor;
    const [tab, setTab] = useState('catalog');
    const [view, setView] = useState(null); // {type:'player', slug} | {type:'editor', courseId}

    if (view?.type === 'player') return <LmsCoursePlayer slug={view.slug} user={user} onBack={() => setView(null)} />;
    if (view?.type === 'editor') return <LmsCourseEditor courseId={view.courseId} user={user} onBack={() => { setView(null); setTab('teach'); }} />;

    const tabs = [
        { id: 'catalog', label: 'Catalogue', icon: BookOpen },
        { id: 'learn', label: 'Mon apprentissage', icon: GraduationCap },
        ...(isInstructor ? [{ id: 'teach', label: 'Espace formateur', icon: PenTool }] : []),
    ];

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--card-border)', padding: '0 24px' }}>
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        style={{ padding: '14px 18px', border: 'none', background: 'transparent', cursor: 'pointer',
                            borderBottom: tab === t.id ? `2px solid ${ACCENT}` : '2px solid transparent',
                            color: tab === t.id ? ACCENT : 'var(--text-secondary)', fontWeight: tab === t.id ? 600 : 400, fontSize: 14,
                            display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <t.icon size={15} /> {t.label}
                    </button>
                ))}
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
                {tab === 'catalog' && <Catalog onOpen={slug => setView({ type: 'player', slug })} />}
                {tab === 'learn' && <MyLearning onOpen={slug => setView({ type: 'player', slug })} />}
                {tab === 'teach' && <Teach onEdit={courseId => setView({ type: 'editor', courseId })} />}
            </div>
        </div>
    );
}

function Catalog({ onOpen }) {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => { lmsApi.listPublished().then(r => setCourses(r.courses || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

    const act = async (c) => {
        try {
            if (c.price_eur && Number(c.price_eur) > 0) {
                const { url } = await lmsApi.checkout(c.id);
                window.location.href = url;
            } else {
                await lmsApi.enroll(c.id); onOpen(c.slug);
            }
        } catch (e) { alert(e.message); }
    };

    if (loading) return <Pad>Chargement…</Pad>;
    if (!courses.length) return <Pad><Empty icon={BookOpen} text="Aucun cours publié pour le moment." /></Pad>;
    return (
        <Pad>
            <Grid>
                {courses.map(c => (
                    <Card key={c.id}>
                        {c.cover_image_url && <img src={c.cover_image_url} alt="" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8 }} />}
                        <h3 style={{ margin: '8px 0 4px', fontSize: 16 }}>{c.title}</h3>
                        <p style={{ fontSize: 13, color: 'var(--text-secondary)', minHeight: 36 }}>{c.subtitle || c.description?.slice(0, 80)}</p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                            <strong>{c.price_eur ? `${c.price_eur} €` : 'Gratuit'}</strong>
                            <button onClick={() => act(c)} style={primaryBtn}>
                                {c.price_eur ? <><ShoppingCart size={14} /> Acheter</> : <>Commencer <ArrowRight size={14} /></>}
                            </button>
                        </div>
                    </Card>
                ))}
            </Grid>
        </Pad>
    );
}

function MyLearning({ onOpen }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => { lmsApi.myEnrollments().then(r => setItems(r.enrollments || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
    if (loading) return <Pad>Chargement…</Pad>;
    if (!items.length) return <Pad><Empty icon={GraduationCap} text="Tu n'es inscrit à aucun cours. Va au catalogue." /></Pad>;
    return (
        <Pad>
            <Grid>
                {items.map(e => (
                    <Card key={e.id} onClick={() => onOpen(e.slug)} clickable>
                        <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>{e.title}</h3>
                        <div style={{ background: 'var(--card-border)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                            <div style={{ width: `${e.progress_pct}%`, height: '100%', background: Number(e.progress_pct) >= 100 ? '#22c55e' : ACCENT }} />
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>{Number(e.progress_pct)}% · {e.status}</div>
                    </Card>
                ))}
            </Grid>
        </Pad>
    );
}

function Teach({ onEdit }) {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const reload = () => { setLoading(true); lmsApi.myCourses().then(r => setCourses(r.courses || [])).catch(() => {}).finally(() => setLoading(false)); };
    useEffect(reload, []);

    if (loading) return <Pad>Chargement…</Pad>;
    return (
        <Pad>
            {creating && (
                <LmsCourseFormModal
                    onClose={() => setCreating(false)}
                    onSaved={(course) => { setCreating(false); onEdit(course.id); }}
                />
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>Mes cours</h2>
                <button onClick={() => setCreating(true)} style={primaryBtn}><Plus size={14} /> Nouveau cours</button>
            </div>
            {!courses.length ? <Empty icon={PenTool} text="Aucun cours créé. Lance-toi !" /> : (
                <Grid>
                    {courses.map(c => (
                        <Card key={c.id} onClick={() => onEdit(c.id)} clickable>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>{c.title}</h3>
                                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: c.is_published ? 'rgba(34,197,94,0.15)' : 'var(--card-border)', color: c.is_published ? '#16a34a' : 'var(--text-secondary)' }}>
                                    {c.is_published ? 'Publié' : 'Brouillon'}
                                </span>
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.module_count} module(s) · {c.enrollment_count} apprenant(s)</div>
                        </Card>
                    ))}
                </Grid>
            )}
        </Pad>
    );
}

const Pad = ({ children }) => <div style={{ padding: '24px 32px', height: '100%', overflowY: 'auto' }}>{children}</div>;
const Grid = ({ children }) => <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>{children}</div>;
const Card = ({ children, onClick, clickable }) => (
    <div onClick={onClick} style={{ border: '1px solid var(--card-border)', borderRadius: 12, padding: 16, background: 'var(--card-bg)', cursor: clickable ? 'pointer' : 'default' }}>{children}</div>
);
const Empty = ({ icon: Icon, text }) => (
    <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '60px 0' }}>
        <Icon size={40} style={{ opacity: 0.4 }} /><p>{text}</p>
    </div>
);
const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 8, background: ACCENT, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 };
